import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SearchResultGroup } from '@coin/shared/group-search/util';
import { ClickedOutsideDirective } from '@coin/shared/core/util-directives';
import { GlobalEventsService } from '@coin/shared/core/util-helpers';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'coin-group-search-bar',
  templateUrl: './group-search-bar.component.html',
  styleUrls: ['./group-search-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [
    {
      directive: ClickedOutsideDirective,
      outputs: ['clickedOutside']
    }
  ],
  standalone: false
})
export class GroupSearchBarComponent implements AfterViewInit {
  private events = inject(GlobalEventsService);

  @ViewChild('searchInputElement') searchInputElement: ElementRef;

  @Input() public hasInitialFocus = false;
  @Input({ required: true }) searchResultGroups: SearchResultGroup[];
  @Input() resultCacheKey: string;
  @Input() placeholder = 'general.search-placeholder';
  @Input() isCosmos = true;
  @Output() initializeSearchLambda = new EventEmitter();

  public searchInput: string;

  public isSearchResultVisible$ = new BehaviorSubject<boolean>(false);

  @HostListener('focusin')
  public onFocusEnter(): void {
    if (!this.isSearchResultVisible$.value) {
      this.isSearchResultVisible$.next(true);
    }
  }

  @HostListener('clickedOutside')
  public handleClickedOutside() {
    this.isSearchResultVisible$.next(false);
  }

  public onEscape(): void {
    if (this.isSearchResultVisible$.value) {
      this.isSearchResultVisible$.next(false);
      this.searchInputElement.nativeElement.blur();
    }
  }

  constructor() {
    this.events
      .listen('keydown')
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        if (event.key === 'Escape') {
          this.onEscape();
        }
      });
  }

  public ngAfterViewInit(): void {
    if (this.hasInitialFocus) {
      this.searchInputElement?.nativeElement?.focus();
    }
  }

  public onInputChange(value: string): void {
    this.searchInput = value;
  }

  public clearSearch(): void {
    this.searchInput = '';
  }

  public focusInput(): void {
    this.searchInputElement.nativeElement.focus();
  }

  public onNavigatedToResult(): void {
    this.onInputChange('');
    this.isSearchResultVisible$.next(false);
  }
}
